import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NavigationFeedbackStore } from '@core/services/navigation-feedback.store';
import { MobileNavigationLoaderComponent } from '@shared/components/mobile-navigation-loader/mobile-navigation-loader.component';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { TranslateService } from '@core/services/translate.service';
import { effect } from '@angular/core';

/**
 * Slim status strip that surfaces during slow mobile navigations.
 *
 * Visibility ladder (driven by NavigationFeedbackStore.tier):
 *   - tier <= 2 : hidden (existing per-button spinner + top progress bar suffice).
 *   - tier == 3 : shows app.nav.slowConnection.hint.
 *   - tier == 4 : adds app.nav.slowConnection.cancel.
 *   - tier == 5 : auto-dismisses, raises a single toast.
 *
 * Mounted globally from app.component.html in the mobile branch only.
 */
@Component({
  selector: 'app-mobile-slow-connection-hint',
  standalone: true,
  imports: [CommonModule, MobileNavigationLoaderComponent, TranslatePipe],
  templateUrl: './mobile-slow-connection-hint.component.html',
  styleUrl: './mobile-slow-connection-hint.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileSlowConnectionHintComponent {
  private readonly feedback = inject(NavigationFeedbackStore);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);

  protected readonly tier = this.feedback.tier;
  protected readonly kind = this.feedback.kind;

  protected readonly visible = computed(() => {
    const t = this.tier();
    return t === 3 || t === 4;
  });

  // Hide the cancel button for back navigation: cancelling "back" would mean
  // staying on the page the user explicitly tried to leave, which feels broken.
  protected readonly showCancel = computed(
    () => this.tier() === 4 && this.kind() === 'forward',
  );

  constructor() {
    let toastShownForCycle = false;

    effect(() => {
      const t = this.tier();
      if (t === 5 && !toastShownForCycle) {
        toastShownForCycle = true;
        this.message.warning(
          this.translate.instant('app.nav.slowConnection.toast'),
          // Dedupe across rapid retries — only one toast alive at a time.
          { nzDuration: 5000, nzAnimate: true },
        );
        // Auto-recover: clear pending state so the buttons are tappable again.
        this.feedback.finish();
      }
      if (t === 0) {
        toastShownForCycle = false;
      }
    });
  }

  protected onCancel(): void {
    this.feedback.cancel();
  }
}
